import * as Yup from "yup";
import {
  EMAIL_REGEX,
  MOBILE_REGEX,
  NOSPACE_REGEX,
  PASSWORDS_REGEX,
} from "./constants";
import { MESSAGE } from "./validationMessage";
import dayjs from "dayjs";

// Helper function to add trim validation to required string fields
export const requiredStringWithTrim = (message = MESSAGE.REQUIRED) => {
  return Yup.string()
    .trim()
    .required(message)
    .test('not-only-spaces', message, (value) => {
      return value && value.trim().length > 0;
    });
};

export let email = Yup.string()
  .trim()
  .max(254, "Email should not be longer than 254 characters")
  .required(MESSAGE.REQUIRED)
  .email(MESSAGE.EMAIL)
  .matches(EMAIL_REGEX, MESSAGE.EMAIL)
  .test('not-only-spaces', MESSAGE.REQUIRED, (value) => {
    return value && value.trim().length > 0;
  });

let name = Yup.string()
  .trim()
  .min(2, "Name must be at least 2 characters")
  .max(60, "Name should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
  .test('not-only-spaces', MESSAGE.REQUIRED, (value) => {
    return value && value.trim().length > 0;
  });

let title = Yup.string()
  .trim()
  .max(60, "This field should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
  .test('not-only-spaces', MESSAGE.REQUIRED, (value) => {
    return value && value.trim().length > 0;
  });

let description = Yup.string()
  .trim()
  .max(250, "Description should not be longer than 250 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
  .test('not-only-spaces', MESSAGE.REQUIRED, (value) => {
    return value && value.trim().length > 0;
  });

let question = Yup.string()
  .trim()
  .max(150, "This field should not be longer than 150 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
  .test('not-only-spaces', MESSAGE.REQUIRED, (value) => {
    return value && value.trim().length > 0;
  });

let status = Yup.mixed().required(MESSAGE.REQUIRED);

let mobile = Yup.string().matches(MOBILE_REGEX, MESSAGE.PHONE);


export const CmsSchema = Yup.object({
  title,
  description: Yup.string()
    .trim()
    .required("Description is required")
    .test('not-only-spaces', "Description is required", (value) => {
      return value && value.trim().length > 0;
    }),
  status,
});

const ALPHANUMERIC_WITH_LETTERS_REGEX = /^(?=.*[a-zA-Z])[A-Za-z0-9]*$/;
export const CategorySchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Category name is required")
    .min(2, "Category name must be at least 2 characters")
    .test('not-only-spaces', "Category name is required", (value) => {
      return value && value.trim().length > 0;
    }),
  images: Yup.array()
    .min(1, "At least one image is required")
    .required("Image is required")
    .test('file-type', 'Only PNG, JPG, JPEG, and WEBP formats are allowed', function (value) {
      if (!value || value.length === 0) return true; // Let min validation handle empty case
      return value.every(item => {
        if (typeof item === 'string') return true; // Existing images from server or data URLs
        return item && ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(item.type);
      });
    }),

});

export const BrandSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Brand name is required")
    .min(2, "Brand name must be at least 2 characters")
    .test('not-only-spaces', "Brand name is required", (value) => {
      return value && value.trim().length > 0;
    }),
  description: Yup.string()
    .trim()
    .max(200, "Description should not be longer than 200 characters")
    .nullable(),
});
export const SubcategorySchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Sub Category name is required")
    .min(2, "Sub Category name must be at least 2 characters")
    .test('not-only-spaces', "Sub Category name is required", (value) => {
      return value && value.trim().length > 0;
    }),
  description: Yup.string()
    .trim()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .test('not-only-spaces', "Description is required", (value) => {
      return value && value.trim().length > 0;
    }),
  image: Yup.array()
    .min(1, "At least one image is required")
    .required("Image is required")
    .test('file-type', 'Only PNG, JPG, JPEG, and WEBP formats are allowed', function (value) {
      if (!value || value.length === 0) return true; // Let min validation handle empty case
      return value.every(item => {
        if (typeof item === 'string') return true; // Existing images from server or data URLs
        return item && ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(item.type);
      });
    }),
});

export const BidPackageSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Name is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .test('not-only-spaces', "Name is required", (value) => {
      return value && value.trim().length > 0;
    }),
  price: Yup.number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(1, "Price must be greater than 0")
    .max(10000, "Price must not exceed 10,000")
    .test("price-format", "Invalid price format", (value) => {
      return /^\d+(\.\d{1,2})?$/.test(value);
    }),
  earnBids: Yup.number()
    .typeError("Earn Bids must be a number")
    .required("Earn Bids is required")
    .min(1, "Earn Bids must be greater than 0")
    .max(10000, "Earn Bids must not exceed 10,000")
    .test("earnBids-format", "Invalid Earn Bids format", (value) => {
      return /^\d+(\.\d{1,2})?$/.test(value);
    }),
  description: Yup.array()
    .of(
      Yup.string()
        .trim()
        .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
        .required("Description point cannot be empty")
        .test('not-only-spaces', "Description point cannot be empty", (value) => {
          return value && value.trim().length > 0;
        })
    )
    .min(1, "At least one description point is required"),
});

export const profileSchema = Yup.object().shape({
  name: name,
  phoneNumber: Yup.string()
    .required("Phone number is required")

    .matches(/^[1-9][0-9]{9}$/, "Enter a valid 10 digit phone number"),
  email,
});

export const userSchema = (id) => Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .max(50, "First Name must be at most 50 characters")
    .required("First Name is required"),
  lastName: Yup.string()
    .trim()
    .max(50, "Last Name must be at most 50 characters")
    .required("Last Name is required"),
  email: Yup.string()
    .trim()
    .max(50, "Email must be at most 50 characters")
    .email("Invalid email address")
    .required("Email is required"),
  countryCode: Yup.string()
    .trim()
    .required("Country code is required")
    .test('not-only-spaces', "Country code is required", (value) => {
      return value && value.trim().length > 0;
    }),

  phoneNumber: Yup.string()
    .trim()
    .required("Phone Number is required")
    .matches(/^[0-9]{10}$/, "Phone Number must be exactly 10 digits")
    .test(
      "not-all-zeros",
      "Phone Number cannot be all zeros",
      (value) => value !== "0000000000"
    ),
  dob: Yup.date()
    .nullable()
    .required("Date of Birth is required")
    .test('is-valid-date', 'Please enter a valid date', function (value) {
      if (!value) return true; // Let required validation handle empty values
      return value instanceof Date && !isNaN(value.getTime());
    })
    .test('is-not-future', 'Date of Birth cannot be in the future', function (value) {
      if (!value) return true;
      return value <= new Date();
    })
    .test('is-adult', 'Must be at least 18 years old', function (value) {
      if (!value) return true;
      const today = new Date();
      const birthDate = new Date(value);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      return age > 18 || (age === 18 && monthDiff >= 0);
    }),
  driverLicence: Yup.string()
    .trim()
    .max(50, "Driver License must be at most 50 characters")
    .nullable()
    .notRequired(),
  role: Yup.string()
    .trim()
    .required("Role is required")
    .test('not-only-spaces', "Role is required", (value) => {
      return value && value.trim().length > 0;
    }),
  password: id
    ? Yup.string().notRequired()
    : Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
        "Password must contain uppercase, lowercase, number, and special character"
      ),
  street: Yup.string()
    .trim()
    .max(100, "Street must be at most 100 characters")
    .required("Street is required"),
  addressCity: Yup.string()
    .trim()
    .max(50, "City must be at most 50 characters")
    .required("City is required"),
  province: Yup.string()
    .trim()
    .max(50, "Province must be at most 50 characters")
    .required("Province is required"),
  country: Yup.string()
    .trim()
    .max(50, "Country must be at most 50 characters")
    .required("Country is required"),
    postalCode: Yup.string()
    .matches(/^(?=.*[A-Za-z0-9])[A-Za-z0-9 ]+$/, "Only letters, numbers, or spaces are allowed")
    .min(3, "Minimum 3 character allowed")
    .max(10, "Maximum 10 characters allowed")
    .required("Postal code is required"),
  // Dealer fields
  businessName: Yup.string().when("role", (role, schema) => {
    const actualRole = Array.isArray(role) ? role[0] : role;
    return actualRole === "Dealer"
      ? schema
        .trim()
        .max(100, "Business Name must be at most 100 characters")
        .required("Business Name is required")
      : schema.strip();
  }),
  dealershipNumber: Yup.string().when("role", (role, schema) => {
    const actualRole = Array.isArray(role) ? role[0] : role;
    return actualRole === "Dealer"
      ? schema
        .trim()
        .max(50, "Dealership Number must be at most 50 characters")
        .required("Dealership Number is required")
      : schema.strip();
  }),
  taxNumber: Yup.string().when("role", (role, schema) => {
    const actualRole = Array.isArray(role) ? role[0] : role;
    return actualRole === "Dealer"
      ? schema
        .trim()
        .max(50, "Tax Number must be at most 50 characters")
        .required("Tax Number is required")
      : schema.strip();
  }),

});

export const subadminSchema = (isEdit = false) =>
  Yup.object().shape({
    name: name, // keep your existing validation
    phoneNumber: Yup.string()
      .trim()
      .required(MESSAGE.REQUIRED)
      .matches(MOBILE_REGEX, MESSAGE.PHONE)
      .test("not-all-zeros", MESSAGE.PHONE, (value) => value !== "0000000000"),
    permissions: Yup.array()
      .of(Yup.string())
      .min(1, "At least one permission is required")
      .required("Permissions are required"),
    password: isEdit
      ? Yup.string().nullable()
      : Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(
          PASSWORDS_REGEX,
          "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
        ),
    confirmPassword: isEdit
      ? Yup.string().nullable()
      : Yup.string()
        .required("Confirm Password is required")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
  });

export const TicketSchema = Yup.object().shape({

  name: Yup.string()
    .trim()
    .required("Category name is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Category name must be at least 2 characters")
    .test('not-only-spaces', "Category name is required", (value) => {
      return value && value.trim().length > 0;
    }),
  image: Yup.mixed().required("Image is required"),
  venueImage: Yup.mixed().required("Venue image is required"),
  bidPrice: Yup.string()
    .required("Bid start price is required")
    .matches(
      /^(0|[1-9]\d*)(\.\d{1,2})?$/,
      "Bid start price must be a valid number with up to 2 decimal places"
    )
    .test(
      "is-greater-than-zero",
      "Bid start price must be greater than 0",
      function (value) {
        return parseFloat(value || "0") > 0;
      }
    )
    .test(
      "is-less-than-price",
      "Bid start price must be less than the price",
      function (value) {
        const { price } = this.parent;
        return parseFloat(value || "0") < parseFloat(price || "0");
      }
    ),

  price: Yup.string()
    .required("Price is required")
    .matches(
      /^(0|[1-9]\d*)(\.\d{1,2})?$/,
      "Price must be a valid number without leading zeros and up to 2 decimal places"
    )
    .test(
      "is-greater-than-zero",
      "Price must be greater than 0",
      function (value) {
        return parseFloat(value || "0") > 0;
      }
    ),
  totalTicket: Yup.string()
    .required("Total ticket is required")
    .matches(/^[0-9]+$/, "Total ticket must be greater than 0")
    .min(1, "Total ticket must be greater than 0"),
  slug: Yup.string()
    .trim()
    .required("Category type is required")
    .test('not-only-spaces', "Category type is required", (value) => {
      return value && value.trim().length > 0;
    }),
  categoryId: Yup.string()
    .trim()
    .required("Category is required")
    .test('not-only-spaces', "Category is required", (value) => {
      return value && value.trim().length > 0;
    }),

  description: Yup.string()
    .trim()
    .required("Description  is required")
    .min(20, "Description name must be at least 20 characters")
    .max(500, "Description should not exceed 500 characters"),

  teamFirst: Yup.string().when("slug", {
    is: (slug) => slug === "sports",
    then: (schema) => schema
      .trim()
      .required("Team 1 name is required")
      .test('not-only-spaces', "Team 1 name is required", (value) => {
        return value && value.trim().length > 0;
      }),
    otherwise: (schema) => schema.notRequired(),
  }),

  teamSecond: Yup.string().when("slug", {
    is: (slug) => slug === "sports",
    then: (schema) => schema
      .trim()
      .required("Team 2 name is required")
      .test('not-only-spaces', "Team 2 name is required", (value) => {
        return value && value.trim().length > 0;
      }),
    otherwise: (schema) => schema.notRequired(),
  }),

  artistName: Yup.string().when("slug", {
    is: (slug) => slug !== "sports",
    then: (schema) => schema
      .trim()
      .required("Artist name is required")
      .test('not-only-spaces', "Artist name is required", (value) => {
        return value && value.trim().length > 0;
      }),
    otherwise: (schema) => schema.notRequired(),
  }),

  // New fields
  city: Yup.mixed().required("City is required"),
  state: Yup.string()
    .trim()
    .required("State is required")
    .test('not-only-spaces', "State is required", (value) => {
      return value && value.trim().length > 0;
    }),
  venue: Yup.string()
    .trim()
    .required("Venue is required")
    .test('not-only-spaces', "Venue is required", (value) => {
      return value && value.trim().length > 0;
    }),
  country: Yup.string()
    .trim()
    .required("Country is required")
    .test('not-only-spaces', "Country is required", (value) => {
      return value && value.trim().length > 0;
    }),

  zipcode: Yup.string()
    .trim()
    .required("Zip code is required")
    .matches(/^\d{4,6}$/, "Zip code must be between 4 and 6 digits"),
  fullAddress: Yup.string()
    .trim()
    .required("Full address is required")
    .min(10, "Full address must be at least 10 characters")
    .test('not-only-spaces', "Full address is required", (value) => {
      return value && value.trim().length > 0;
    }),
  event_start_date: Yup.date()
    .nullable()
    .required("Event start date is required")
    .min(new Date(), "Event start date cannot be in the past"),

  section: Yup.string()
    .trim()
    .required("Section is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(1, "Section must be at least 2 characters")
    .test('not-only-spaces', "Section is required", (value) => {
      return value && value.trim().length > 0;
    }),

  row: Yup.string()
    .trim()
    .required("Row is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(1, "Row must be at least 1 characters")
    .test('not-only-spaces', "Row is required", (value) => {
      return value && value.trim().length > 0;
    }),
});

export const auctionSchema = Yup.object({
  vehicleId: Yup.string()
    .trim()
    .required("Vehicle is required")
    .test('not-only-spaces', "Vehicle is required", (value) => {
      return value && value.trim().length > 0;
    }),
  auctionType: Yup.string()
    .trim()
    .required("Auction type is required")
    .oneOf(["normal", "marketDropAi", "claimPoint"], "Invalid auction type")
    .test('not-only-spaces', "Auction type is required", (value) => {
      return value && value.trim().length > 0;
    }),
  startTime: Yup.date()
    .typeError("Start time is required")
    .required("Start time is required")
    .min(new Date(), "Start time cannot be in the past"),
  endTime: Yup.date()
    .typeError("End time is required")
    .required("End time is required")
    .min(Yup.ref("startTime"), "End time must be after start time"),

  // description: Yup.string()
  //   .trim()
  //   .required("Description is required")
  //   .min(10, "Description must be at least 10 characters")
  //   .max(1000, "Description must not exceed 1000 characters")
  //   .test('not-only-spaces', "Description is required", (value) => {
  //     return value && value.trim().length > 0;
  //   }),
  defaultPriceDropIntervalValue: Yup.number()
    .nullable()
    .notRequired()
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    })
    .test('number-format', 'Price drop interval value must be a number', function(value) {
      if (value === null || value === undefined || value === '') return true;
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) return false;
      return true;
    })
    .test('min-value', 'Must be at least 1', function(value) {
      if (value === null || value === undefined || value === '') return true;
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return numValue >= 1;
    })
    .test('number-format-digits', 'Maximum 8 digits and 2 decimal places allowed', function(value) {
      if (value === null || value === undefined || value === '') return true;
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) return true;
      const str = String(numValue);
      const parts = str.split('.');
      const integerPart = parts[0].replace(/^-/, '');
      const totalDigits = integerPart.length + (parts[1] ? parts[1].length : 0);
      if (totalDigits > 8) return false;
      if (parts[1] && parts[1].length > 2) return false;
      return true;
    }),
  defaultPriceDropIntervalUnit: Yup.string()
    .trim()
    .nullable()
    .notRequired()
    .oneOf(["seconds", "minutes", "hours", "days", null, ""], "Invalid unit"),
  defaultPriceDropValue: Yup.number()
    .nullable()
    .notRequired()
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    })
    .test('number-format', 'Price drop value must be a number', function(value) {
      if (value === null || value === undefined || value === '') return true;
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) return false;
      return true;
    })
    .test('min-value', 'Must be at least 1', function(value) {
      if (value === null || value === undefined || value === '') return true;
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return numValue >= 1;
    })
    .test('number-format-digits', 'Maximum 8 digits and 2 decimal places allowed', function(value) {
      if (value === null || value === undefined || value === '') return true;
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) return true;
      const str = String(numValue);
      const parts = str.split('.');
      const integerPart = parts[0].replace(/^-/, '');
      const totalDigits = integerPart.length + (parts[1] ? parts[1].length : 0);
      if (totalDigits > 8) return false;
      if (parts[1] && parts[1].length > 2) return false;
      return true;
    }),
});

export const FreeBidManagementSchema = Yup.object().shape({
  type: Yup.string()
    .trim()
    .required("Type is required.")
    .oneOf(["daily", "weekly"], "Invalid type selected.")
    .test('not-only-spaces', "Type is required.", (value) => {
      return value && value.trim().length > 0;
    }),
  freeBid: Yup.number()
    .required("Free bids are required.")
    .positive("Free bids must be greater than 0"),
  videoLength: Yup.number()
    .required("Video length is required.")
    .min(5, "Video length must be greater than 5 seconds")
    .max(60, "Video length should not be longer than 60 seconds"),
  description: Yup.string()
    .trim()
    .required(MESSAGE.REQUIRED)
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(3, "Description must be at least 3 characters")
    .max(50, "Description should not be longer than 250 characters")
    .test('not-only-spaces', MESSAGE.REQUIRED, (value) => {
      return value && value.trim().length > 0;
    }),
  thumbnailImage: Yup.mixed().required("Thumbnail image is required."),
  video: Yup.mixed().required("Video is required."),
  status,
});

export const AuctionTaskSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("Title is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Title name must be at least 2 characters")
    .max(100, "Title cannot exceed 100 characters")
    .test('not-only-spaces', "Title is required", (value) => {
      return value && value.trim().length > 0;
    }),

  purchaseRange: Yup.number()
    .typeError("Purchase range must be a number")
    .required("Purchase range is required")
    .min(0.01, "Purchase range must be at least 0.01")
    .max(9999, "Purchase range cannot exceed 9999"),

  freeBidsGranted: Yup.number()
    .typeError("Free bids must be a number")
    .required("Free bids are required")
    .min(0.01, "Free bids must be at least 0.01")
    .max(500, "Free bids cannot exceed 500"),

  notice: Yup.string()
    .trim()
    .required("Notice is required")
    .min(10, "Notice must be at least 10 characters long")
    .test('not-only-spaces', "Notice is required", (value) => {
      return value && value.trim().length > 0;
    }),

  image: Yup.mixed().test(
    "required",
    "Image is required",
    (value) => value instanceof File || !!value
  ),

  status: Yup.boolean().required("Status is required"),
});

export const ShareTaskSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Title name must be at least 2 characters")
    .required("Title is required")
    .max(100, "Title must not exceed 100 characters")
    .test('not-only-spaces', "Title is required", (value) => {
      return value && value.trim().length > 0;
    }),
  shareNumber: Yup.number()
    .typeError("Share Number must be a valid integer")
    .integer("Share Number must be a whole number")
    .required("Share Number is required")
    .min(1, "Share range must be at least 1")
    .max(9999, "Share range cannot exceed 9999"),
  freeBidsGranted: Yup.number()
    .required("Free Bids is required")
    .min(0.01, "Free Bids must be at least 0.01")
    .max(500, "Free Bids cannot exceed 500"),
  notice: Yup.string()
    .trim()
    .required("Notice is required")
    .min(10, "Notice must be at least 10 characters long")
    .test('not-only-spaces', "Notice is required", (value) => {
      return value && value.trim().length > 0;
    }),
  image: Yup.mixed().test(
    "required",
    "Image is required",
    (value) => value instanceof File || !!value
  ),

  status: Yup.boolean().required("Status is required"),
});

export const PlaceBidTaskSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Title name must be at least 2 characters")
    .required("Title is required")
    .max(9999, "Title must not exceed 9999 characters")
    .test('not-only-spaces', "Title is required", (value) => {
      return value && value.trim().length > 0;
    }),
  shareBids: Yup.number()
    .typeError("Place Bids must be a valid integer")
    .integer("Place Bids must be a whole number")
    .required("Place Bids is required")
    .min(1, "Purchase range must be at least 1")
    .max(1000, "Purchase range cannot exceed 100"),
  freeBidsGranted: Yup.number()
    .required("Free Bids is required")
    .min(0.01, "Free Bids must be at least 0.01")
    .max(500, "Free Bids cannot exceed 500"),
  notice: Yup.string()
    .trim()
    .required("Notice is required")
    .min(10, "Notice must be at least 10 characters long")
    .test('not-only-spaces', "Notice is required", (value) => {
      return value && value.trim().length > 0;
    }),
  image: Yup.mixed().test(
    "required",
    "Image is required",
    (value) => value instanceof File || !!value
  ),

  status: Yup.boolean().required("Status is required"),
});



export const validationSchema = Yup.object({
  vehicleName: Yup.string()
    .required("Stock Number is required")
    .test('trim', 'Stock Number cannot be only spaces', value => value && value.trim().length > 0)
    // .matches(/^[a-zA-Z0-9]+$/, "Stock Number must contain only letters and numbers")
    .max(20, "Stock Number must be less than 20 characters"),
  originalPrice: Yup.number()
    .required("Original Price is required")
    .min(0, "Original Price cannot be negative")
    .max(99999999, "Original Price seems too high"),
  make: Yup.string()
    .required("Make is required")
    .test('trim', 'Make cannot be only spaces', value => value && value.trim().length > 0)
    .max(50, "Make must be less than 50 characters"),
  model: Yup.string()
    .required("Model is required")
    .test('trim', 'Model cannot be only spaces', value => value && value.trim().length > 0)
    .max(50, "Model must be less than 50 characters"),
  year: Yup.number()
    .integer("Year must be a whole number")
    .required("Year is required")
    .min(1900, "Year must be after 1900")
    .max(new Date().getFullYear() + 1, "Year cannot be in the future"),
  vin: Yup.string()
    .trim()
    .required("VIN is required")
    .test('not-only-spaces', "VIN is required", (value) => {
      return value && value.trim().length > 0;
    }),
  fuelType: Yup.string()
    .trim()
    .required("Fuel type is required")
    .test('not-only-spaces', "Fuel type is required", (value) => {
      return value && value.trim().length > 0;
    }),
  transmissionType: Yup.string()
    .trim()
    .required("Transmission type is required")
    .test('not-only-spaces', "Transmission type is required", (value) => {
      return value && value.trim().length > 0;
    }),
  interiorColor: Yup.string()
    .required("Interior colour is required")
    .test('trim', 'Interior colour cannot be only spaces', value => value && value.trim().length > 0)
    .max(30, "Interior colour must be less than 30 characters"),
  exteriorColor: Yup.string()
    .required("Exterior colour is required")
    .test('trim', 'Exterior colour cannot be only spaces', value => value && value.trim().length > 0)
    .max(30, "Exterior colour must be less than 30 characters"),
  category: Yup.array().min(1, "At least one category is required"),
  description: Yup.string()
    .required("Description is required")
    .test('trim', 'Description cannot be only spaces', value => value && value.trim().length > 0)
    .max(1000, "Description must be less than 1000 characters"),
  MVP: Yup.string()
    .required("MVP is required")
    .test('trim', 'MVP cannot be only spaces', value => value && value.trim().length > 0)
    .max(1000, "MVP must be less than 1000 characters"),
  miles: Yup.number()
    .required("Miles is required")
    .min(0, "Miles cannot be negative")
    .max(999999, "Miles seems too high"),
  odometerUnit: Yup.string()
    .trim()
    .required("Odometer unit is required")
    .oneOf(["KM", "MI"], "Unit must be either KM or MI")
    .test('not-only-spaces', "Odometer unit is required", (value) => {
      return value && value.trim().length > 0;
    }),
  trim: Yup.string()
    .required("Trim is required")
    .test('trim', 'Trim cannot be only spaces', value => value && value.trim().length > 0)
    .max(50, "Trim must be less than 50 characters"),
  drivetrain: Yup.string()
    .required("Drivetrain is required")
    .test('trim', 'Drivetrain cannot be only spaces', value => value && value.trim().length > 0)
    .max(20, "Drivetrain must be less than 20 characters"),
  address: Yup.string()
    .required("Address is required")
    .test('trim', 'Address cannot be only spaces', value => value && value.trim().length > 0)
    .max(200, "Address must be less than 200 characters"),
  city: Yup.string()
    .required("City is required")
    .test('trim', 'City cannot be only spaces', value => value && value.trim().length > 0)
    .max(50, "City must be less than 50 characters"),
    postalCode: Yup.string()
    .matches(/^(?=.*[A-Za-z0-9])[A-Za-z0-9 ]+$/, "Only letters, numbers, or spaces are allowed")
    .min(3, "Minimum 3 character allowed")
    .max(10, "Maximum 10 characters allowed")
    .required("Postal code is required"),
  country: Yup.string()
    .required("Country is required")
    .test('trim', 'Country cannot be only spaces', value => value && value.trim().length > 0)
    .max(50, "Country must be less than 50 characters"),
  latitude: Yup.string().nullable(),
  longitude: Yup.string().nullable(),
  vehicleImages: Yup.array()
    .min(1, "At least one vehicle image is required")
    .test('file-type', 'Only PNG, JPG, JPEG, and WEBP formats are allowed', function (value) {
      if (!value || value.length === 0) return true; // Let min validation handle empty case
      return value.every(item => {
        if (typeof item === 'string') return true; // Existing images from server or data URLs
        return item && ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(item.type);
      });
    }),
});


export const bannerAndOffer = (id) => Yup.object().shape({
  name: Yup.string()
    .trim()
    .max(100, 'Banner name is too long. It cannot exceed 100 characters.')
    .nullable()
    .notRequired(),

  description: Yup.string()
    .trim()
    .max(200, 'Description is too long. It cannot exceed 200 characters.')
    .nullable()
    .notRequired(),

  startDate: Yup.date()
    .test('required-if-no-id', 'Start Date is required', function (value) {
      const { path, createError } = this;
      if (!id) {
        if (!value) {
          return createError({ path, message: 'Start Date is required' });
        }
        const currentDate = new Date();
        if (new Date(value) < currentDate) {
          return createError({ path, message: 'Start Date cannot be in the past' });
        }
      }
      return true;
    }),

  endDate: Yup.date()
    .test('required-if-no-id', 'End Date is required', function (value) {
      const { path, createError } = this;
      const { startDate } = this.parent;
      if (!id) {
        if (!value) {
          return createError({ path, message: 'End Date is required' });
        }
        const startTime = new Date(startDate);
        const endTime = new Date(value);
        if (endTime <= startTime || (endTime - startTime) < 30 * 60 * 1000) {
          return createError({ path, message: 'End Date must be at least 30 minutes after Start Date' });
        }
      }
      return true;
    })
    .test('end-date-after-current', 'End Date must be after current time', function (value) {
      const { path, createError } = this;
      if (value) {
        const currentTime = new Date();
        const endTime = new Date(value);
        if (endTime <= currentTime) {
          return createError({ path, message: 'End Date must be after current time' });
        }
      }
      return true;
    })
    .test('end-date-after-start', 'End Date must be after Start Date', function (value) {
      const { path, createError } = this;
      const { startDate } = this.parent;
      if (value && startDate) {
        const startTime = new Date(startDate);
        const endTime = new Date(value);
        if (endTime <= startTime) {
          return createError({ path, message: 'End Date must be after Start Date' });
        }
      }
      return true;
    }),

  image: Yup.mixed()
    .test('image', 'Image is required', function (value) {
      const imagePreview = this.parent.imagePreview;
      return imagePreview || value != null;
    }),

  bannerType: Yup.string()
    .trim()
    .required('Banner type is required')
    .oneOf(['normal', 'promotional'], 'Banner type must be either normal or promotional')
    .test('not-only-spaces', 'Banner type is required', (value) => {
      return value && value.trim().length > 0;
    }),

  auctionId: Yup.string()
    .when('bannerType', {
      is: 'promotional',
      then: (schema) => schema
        .required('Auction selection is required for promotional banners')
        .test('auctionId', 'Please select a valid auction', function (value) {
          return value && value.trim() !== '';
        }),
      otherwise: (schema) => schema.notRequired()
    }),
});


export const validationFaqSchema = Yup.object().shape({
  title: Yup.string()
    .required("Question is required")
    .test("not-only-spaces", "Only spaces are not allowed", (value) => {
      if (!value) return false;
      return value.trim().length > 0;
    })
    .test("no-leading-trailing-spaces", "Spaces are not allowed at the start or end of the text", (value) => {
      if (!value) return false;
      return value === value.trim();
    })
    .matches(NOSPACE_REGEX, "Spaces are not allowed at the start or end of the text.")
    .min(2, "Please enter at least 2 characters")
    .max(100, "Question must not be more than 100 characters."),

  description: Yup.string()
    .required("Description is required")
    .test("not-only-spaces", "Only spaces are not allowed", (value) => {
      if (!value) return false;
      // Remove HTML tags and check if there's actual content
      const textContent = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      return textContent.length > 0;
    })
    .test("no-leading-spaces", "Spaces are not allowed before content", (value) => {
      if (!value) return false;
      // Remove HTML tags and check if it starts with spaces
      const textContent = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
      return !/^\s/.test(textContent); 
    })
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description must not exceed 1000 characters"),
});

export const socialMediaSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Name is required")
    .test('not-only-spaces', "Name is required", (value) => {
      return value && value.trim().length > 0;
    }),
  link: Yup.string()
    .trim()
    .required("URL is required")
    .matches(
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.@~:!$&'()*+,;=%-]*)*\/?$/i,
      'Enter a valid URL'
    )
    .test('not-only-spaces', "URL is required", (value) => {
      return value && value.trim().length > 0;
    }),
  image: Yup.mixed().nullable(),
});
